import React, { FocusEvent, FunctionComponent, useEffect } from "react";
import { QuickEntryRubriek } from "./useQuickEntryRubriek";
import {t, Trans} from "@lingui/macro";
import NumberFormat from "react-number-format";
import { DateChangedEvent, DateInput } from "components/inputs/DateInput";
import {ValueBuitenland} from "../../../domain/types";
import {i18n} from "@lingui/core";

type Value = string | boolean | number | ValueBuitenland | undefined;
export interface TaxCodeChangeEvent {
  value: Value;
  error?: JSX.Element;
  partialEntry?: boolean;
}
export interface Props {
  rubriek: QuickEntryRubriek;
  onChange: (props: TaxCodeChangeEvent) => void;
}

export const TaxCodeField: FunctionComponent<Props> = ({ rubriek, onChange }) => {
  let defaultValue: boolean | string | ValueBuitenland = rubriek.default;
  if(rubriek.type === "bool"){
    defaultValue = true;
  } else if(rubriek.type === "buitenland"){
    defaultValue = {Vol: undefined, Vrijgesteld: undefined, Verlaagd: undefined};
  }
  const [value, setValue] = React.useState<Value>(defaultValue);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    requestAnimationFrame(() => {
      if (document.activeElement === e.target) {
        e.target.select();
      }
    });
  };

  const handleChange = (event: TaxCodeChangeEvent) => {
    setValue(event.value);
    onChange(event);
  };

  const handleDateChange = ({ value, error, isPartialDate }: DateChangedEvent) => {
    handleChange({ value, error, partialEntry: isPartialDate });
  };

  useEffect(() => {
    onChange({ value: defaultValue });
    if (rubriek.value !== undefined) {
        setValue(rubriek.value);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubriek.value, setValue]);

  switch (rubriek.type) {
    case "bool":
      return (
        <>
          <input disabled type="checkbox" checked className="amount_bool" />
          <span id="amounttype">{rubriek.default === "yes" ? <Trans>Ja</Trans> : <Trans>Nee</Trans>}</span>
        </>
      );
    case "date":
      return (
        <DateInput autoFocus onFocus={handleFocus} value={(value as string) || ""} onDateChanged={handleDateChange} />
      );
    case "num":
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => handleChange({ value: value.floatValue })}
          decimalScale={0}
          allowNegative={false}
          autoFocus
          onFocus={handleFocus}
        />
      );
    case "pct":
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => handleChange({ value: value.floatValue })}
          decimalScale={2}
          allowNegative={false}
          decimalSeparator={"."}
          allowedDecimalSeparators={[",", "."]}
          autoFocus
          onFocus={handleFocus}
          suffix={"%"}
        />
      );
    case "buitenland":
      let v = value as ValueBuitenland;
      return (
          <div style={{ float: "left"}}>
            <NumberFormat
                value={v?.Vol as string}
                onValueChange={(value) => {
                  v.Vol = value.floatValue;
                  handleChange({ value: v })
                }}
                decimalScale={2}
                allowNegative={false}
                decimalSeparator={"."}
                allowedDecimalSeparators={[",", "."]}
                autoFocus
                placeholder={i18n._(t`Belgisch`)}
                onFocus={handleFocus}
                suffix={"€"}
                style={{ marginRight: "10px"}}
            />
            <NumberFormat
                value={v?.Vrijgesteld as string}
                onValueChange={(value) => {
                  v.Vrijgesteld = value.floatValue;
                  handleChange({ value: v })
                }}
                decimalScale={2}
                allowNegative={false}
                decimalSeparator={"."}
                allowedDecimalSeparators={[",", "."]}
                placeholder={i18n._(t`Vrijgesteld`)}
                onFocus={handleFocus}
                suffix={"€"}
                style={{ marginRight: "10px"}}
            />
            <NumberFormat
                value={v?.Verlaagd as string}
                onValueChange={(value) => {
                  v.Verlaagd = value.floatValue;
                  handleChange({ value: v })
                }}
                decimalScale={2}
                allowNegative={false}
                decimalSeparator={"."}
                allowedDecimalSeparators={[",", "."]}
                placeholder={i18n._(t`Verminderd`)}
                onFocus={handleFocus}
                suffix={"€"}
                style={{ marginRight: "10px"}}
            />
          </div>
      );
    default:
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => handleChange({ value: value.floatValue })}
          decimalScale={2}
          allowNegative={false}
          decimalSeparator={"."}
          allowedDecimalSeparators={[",", "."]}
          autoFocus
          onFocus={handleFocus}
          suffix={"€"}
        />
      );
  }
};

TaxCodeField.displayName = "TaxCodeField";
